'use strict';

const { queryFirst, queryAll, toggleClass, removeClass } = require('./domUtil');
const { ACTIVE_CLASS, DISABLED_CLASS, DESKTOP_BREAKPOINT } = require('./constants');

let cleanup = null;

/**
 * Initializes the loyalty snapshot carousel functionality
 * Sets up navigation controls, scroll, and event handlers for the loyalty cards carousel.
 * @returns {void}
 */
function initLoyaltySnapshot() {
    // Clean up old listeners first
    if (cleanup) {
        cleanup();
        cleanup = null;
    }

    let activeSheet = queryFirst('#postLoginSheet.show') || queryFirst('#mobileHeader.show');

    if (!activeSheet) {
        const isDesktop = window.matchMedia(DESKTOP_BREAKPOINT);
        const container = isDesktop.matches ? '#postLoginSheet' : '#mobileHeader';
        activeSheet = queryFirst(container);
    }

    if (!activeSheet) {
        return;
    }
    const cardsContainer = queryFirst('.loyalty-cards', activeSheet);
    const nextArrow = queryFirst('#next-arrow', activeSheet);
    const prevArrow = queryFirst('#prev-arrow', activeSheet);
    const dots = queryAll('.nav-dot', activeSheet);
    const cards = queryAll('.loyalty-card', cardsContainer);

    if (!cardsContainer || cards.length <= 1) {
        return;
    }

    cardsContainer.scrollLeft = 0;

    if (!nextArrow || !prevArrow || dots.length === 0) {
        return;
    }

    let currentIndex = 0;
    const totalCards = cards.length;
    let scrollTimeout;

    // eslint-disable-next-line require-jsdoc
    function scrollToCard(index) {
        const card = cards[index];
        if (card) {
            const cardOffsetLeft = card.offsetLeft;
            const cardWidth = card.offsetWidth;
            const containerWidth = cardsContainer.offsetWidth;

            let targetScrollLeft;

            if (index === 0) {
                targetScrollLeft = 0;
            } else if (index === totalCards - 1) {
                targetScrollLeft = cardsContainer.scrollWidth - containerWidth;
            } else {
                targetScrollLeft = cardOffsetLeft - (containerWidth - cardWidth) / 2;
            }

            cardsContainer.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
            });
        }

        dots.forEach((dot, i) => {
            toggleClass(dot, ACTIVE_CLASS, i === index);
        });

        cards.forEach((card, i) => {
            toggleClass(card, ACTIVE_CLASS, i === index);
        });

        currentIndex = index;

        toggleClass(prevArrow, DISABLED_CLASS, index === 0);
        toggleClass(nextArrow, DISABLED_CLASS, index >= totalCards - 1);
    }

    const handleNext = e => {
        e.preventDefault();
        if (currentIndex < totalCards - 1) {
            scrollToCard(currentIndex + 1);
        }
    };

    const handlePrev = e => {
        e.preventDefault();
        if (currentIndex > 0) {
            scrollToCard(currentIndex - 1);
        }
    };

    const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            const containerRect = cardsContainer.getBoundingClientRect();
            const containerCenter = containerRect.left + containerRect.width / 2;

            let visibleIndex = 0;
            let closestDistance = Infinity;

            cards.forEach((card, index) => {
                const cardRect = card.getBoundingClientRect();
                const cardCenter = cardRect.left + cardRect.width / 2;
                const distance = Math.abs(cardCenter - containerCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    visibleIndex = index;
                }
            });

            if (visibleIndex !== currentIndex) {
                dots.forEach((dot, i) => {
                    toggleClass(dot, ACTIVE_CLASS, i === visibleIndex);
                });
                cards.forEach((card, i) => {
                    toggleClass(card, ACTIVE_CLASS, i === visibleIndex);
                });
                currentIndex = visibleIndex;
                toggleClass(prevArrow, DISABLED_CLASS, visibleIndex === 0);
                toggleClass(nextArrow, DISABLED_CLASS, visibleIndex >= totalCards - 1);
            }
        }, 150);
    };

    nextArrow.addEventListener('click', handleNext);
    prevArrow.addEventListener('click', handlePrev);
    cardsContainer.addEventListener('scroll', handleScroll, { passive: true });

    if (dots.length > 0 && cards.length > 0) {
        toggleClass(dots[0], ACTIVE_CLASS, true);
        toggleClass(cards[0], ACTIVE_CLASS, true);
        toggleClass(prevArrow, DISABLED_CLASS, true);
        currentIndex = 0;
    }

    cleanup = () => {
        nextArrow.removeEventListener('click', handleNext);
        prevArrow.removeEventListener('click', handlePrev);
        cardsContainer.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);

        // Reset state
        removeClass(dots, ACTIVE_CLASS);
        removeClass(cards, ACTIVE_CLASS);
        removeClass(nextArrow, DISABLED_CLASS);
        removeClass(prevArrow, DISABLED_CLASS);
        // Reset scroll position
        cardsContainer.scrollLeft = 0;
    };
}

module.exports = function () {
    // Reinitialize when modals are opened
    $(document).on('shown.bs.modal', '#postLoginSheet, #mobileHeader', function () {
        initLoyaltySnapshot();
    });
};
